<template>
  <div>
    <SProgress
      v-if="$store.state.cloud.uploadProgress['cloud-upload']"
      v-model="$store.state.cloud.uploadProgress['cloud-upload']"
      :label="$t('global.upload')"
      class="cloud-progress position-fixed w-100"
      animated
    />

    <sdui-waterfall-search
      v-model="localCloudItems"
      class="cloud-list-waterfall-search"
      :model="File"
      :force-refetch="`${url}-${forceRefetch}`"
      :custom-fetch-method="fetchItems"
      :items-length="files.length"
      :search-bar="searchable"
      :clear-search="clearSearch"
      :no-padding="!hideAction"
      :has-cached-results="files.length > 0"
      :class="[!files.length ? 'no-shadow' : '']"
      @retry-fetch="$emit('retryFetch')"
      @loading="(el) => (loading = el)"
    >
      <template #link v-if="canMoveContent">
        <div>
          <sdui-content-move-card
            v-if="!$wait.waiting('suggested-grade-content-move')"
            :title="contentMoveInfoCardCaptions.title"
            :description="contentMoveInfoCardCaptions.description"
            :left-action-text="contentMoveInfoCardCaptions.leftAction"
            :right-action-text="hasSuggestedChannel ? contentMoveInfoCardCaptions.rightAction : ''"
            class="mb-2"
            :data-cy="hasSuggestedChannel ? 'content-move-card-suggested' : 'content-move-card-unclear'"
            :is-configure-info-card="true"
            :has-suggested-channel="hasSuggestedChannel"
            :card-captions="contentMoveInfoCardCaptions"
            :right-action-disabled="false"
            @left-action="showContentMoveDeclineModal = true"
            @right-action="showContentMoveConfiguration"
          />
        </div>
      </template>

      <template #title v-if="!hideAction">
        <b-row class="mb-2">
          <b-col>
            <!-- show dropdown on desktop -->
            <SDropDown variant="link"
                       toggle-class="text-decoration-none"
                       no-caret
                       no-flip
            >
              <template #button-content>
                <span variant="link" class="btn pl-0 with-icon btn-link">
                  {{ queryName }}
                  <sdui-icon class="icon-chevron-down" />
                </span>
              </template>
              <b-dropdown-item-button
                v-for="(action, i) in sortActions"
                :key="i"
                :class="{ active: orderBy === action.orderBy && orderDir === action.orderDir }"
                class="dropdown-item-sm"
                @click="handleOrderBy(action)"
              >
                <sdui-icon :class="action.icon" class="text-primary" />
                <span class="ml-2"> {{ $t(`cloud.sort.${action.orderDir}.${action.orderBy}`) }} </span>
              </b-dropdown-item-button>
              <li class="dropdown-divider" aria-hidden="true" />
            </SDropDown>
          </b-col>

          <!-- choose multiple / cancel multiple -->
          <b-col>
            <div v-if="files.length >= 2 && $can('upload', cloud)" class="float-right ml-auto">
              <b-button
                variant="link"
                class="pr-0"
                data-test="cloud-list-multi-select-toggle"
                @click="cloudFilesSelectMultiple = !cloudFilesSelectMultiple"
              >
                <span v-if="!cloudFilesSelectMultiple" class="selection-title">
                  <span class="d-none d-sm-block">
                    {{ $t('cloud.files_list.choose_multiple_files') }}
                  </span>
                  <span class="d-block d-sm-none">
                    {{ $t('cloud.files_list.choose_multiple_files_short') }}
                  </span>
                </span>

                <span v-else>
                  {{ $t('global.cancel') }}
                </span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </template>

      <template #content>
        <SList v-if="files && files.length">
          <sdui-list-file-item
            v-for="file in files"
            :key="file.id"
            :file="file"
            :hide-action="hideAction"
            :cloud-files-select-multiple="cloudFilesSelectMultiple"
            :select-all="selectAll"
            :folder-size="$getFolderSize(files)"
            @selected-folder="selectedFolder"
            @delete="deleteFiles"
            @rename="renameFile"
            @move="beforeMove"
            @selected-file="selectedFile"
            @open-details="openDetails"
            @updated-selected-files="setSelectedFilesForMassAction"
            @fetch="updatedFetchedFile"
          />
        </SList>
      </template>

      <template v-if="!search" #noResults>
        <div>
          <div class="d-flex justify-content-center">
            <img aria-hidden="true"
                 :src="emptyStateForFiles"
                 alt=""
                 loading="lazy"
            />
          </div>
          <div class="text-center mb-5 empty-waterfall-search">
            <div v-if="!fileTypeToFetch">
              <h2 class="mb-2 heading">{{ $t('cloud.emptyWaterfallSearch.heading') }}</h2>
              <div class="description">
                {{ $t('cloud.emptyWaterfallSearch.description') }}
              </div>
            </div>
            <div v-else>
              <h2 class="mb-2 heading">{{ $t('cloud.emptyWaterfallSearch.heading_folder_fetch') }}</h2>
            </div>
          </div>

          <div v-if="canUpload" class="text-center">
            <b-button
              size="lg"
              variant="primary"
              data-cy="cloud-add-file-button"
              class="cloud-choose-file-button align-content-center"
              @click="emitUploadAndTrack"
            >
              {{ $t('cloud.emptyWaterfallSearch.submit') }}
            </b-button>
          </div>
        </div>
      </template>
    </sdui-waterfall-search>

    <div v-if="canDisplayDirectoryDownload" class="text-center">
      <SLink appearance="b-link"
             class="btn-block"
             :href="fileParent.meta.download_uri"
             data-cy="cloud-folder-download"
             download
      >
        <span>{{ $t('cloud.files_list.download_current_folder') }}</span>
        <span class="sr-only">"{{ fileParent.name }}"</span>
        <span class="ml-1">{{ $getFileTypeAndFolderSize(files) }}</span>
      </SLink>
    </div>

    <!-- modal for details of the file -->
    <SModal
      v-model="modals.details"
      :return-focus="`#dropdown-${fileDetails.id}__BV_toggle_`"
      hide-footer
      :header-title="$t('cloud.cloud_list.modals.details.title')"
      centered
    >
      <cloud-file-details v-if="modals.details" :details="fileDetails" />
    </SModal>

    <hidden-file-upload
      v-model="showFileUpload"
      :show-preview-before-upload="true"
      @files-chosen="startUploading"
      @close="showFileUpload = false"
    />

    <!-- modal for moving file -->
    <cloud-path-selector
      :model-value="modals.move"
      :return-focus="cloudFilesSelectMultiple ? '#cloud-list-multi-select-move' : `#dropdown-${fileToMove}__BV_toggle_`"
      :channel="channel"
      :cloud="cloud"
      share-dialog
      @close="modals.move = false"
      @selected-folder="moveFile"
    />

    <div
      class="fixed-bottom fixed-with-shadow bottom-action-bar"
      :class="cloudFilesSelectMultiple ? 'bar-show' : 'bar-hide'"
      :aria-hidden="cloudFilesSelectMultiple ? 'false' : 'true'"
    >
      <b-container class="d-flex justify-content-between p-0">
        <b-button variant="link"
                  data-test="cloud-list-multi-select-all"
                  @click="selectAll = !selectAll"
        >
          <sdui-icon class="icon-stack-o d-block" />
          {{ $t('cloud.files_list.select_all') }}
        </b-button>

        <!--eslint-disable-next-line sdui/require-focus-restore-on-disabled-button-->
        <b-button
          id="cloud-list-multi-select-move"
          :disabled="!selectedFiles.length"
          variant="link"
          data-test="cloud-list-multi-select-move"
          @click="modals.move = true"
        >
          <sdui-icon class="icon-share-o d-block" />
          {{ $t('global.move') }}
        </b-button>

        <!--eslint-disable-next-line sdui/require-focus-restore-on-disabled-button-->
        <b-button
          :disabled="!selectedFiles.length"
          variant="link"
          data-test="cloud-list-multi-select-delete"
          @click="showDeleteConfirmationModal = true"
        >
          <sdui-loader v-if="$wait.waiting('deleting-multiple-files')" class="mr-4 pr-2" />
          <sdui-icon v-else class="icon-delete-o d-block" />
          {{ $t('global.delete') }}
        </b-button>
      </b-container>
    </div>

    <SConfirmModal
      v-model="showDeleteConfirmationModal"
      :title="$t('cloud.files_list.modal.confirmation.delete.title')"
      :description="$t('cloud.files_list.modal.confirmation.delete.description')"
      :ok-title="$t('cloud.files_list.modal.confirmation.delete.submit')"
      :cancel-title="$t('cloud.files_list.modal.confirmation.delete.cancel')"
      :ok-disabled="$wait.waiting('deleting-files')"
      @ok="deleteFiles()"
      @hide="showDeleteConfirmationModal = false"
    />

    <SConfirmModal
      v-model="showContentMoveDeclineModal"
      data-cy="content-move-decline-modal"
      :title="$t('ui.content_move.decline_modal.title')"
      @hide="showContentMoveDeclineModal = false"
    >
      <sdui-content-move-decline
        :is-channel-of-current-school-year="isChannelOfCurrentSchoolYear"
        @decline-content-move="declineContentMove"
        @close="showContentMoveDeclineModal = false"
      />
    </SConfirmModal>

    <SModal
      v-model="showContentMoveConfigurationModal"
      :header-title="$t('ui.content_move.configuration_modal.title')"
      :show-footer-gradient="false"
      return-focus="#content-move-card-right-action"
      data-cy="content-move-configuration-modal"
    >
      <sdui-content-move-configuration
        v-if="showContentMoveConfigurationModal"
        :channel="channel"
        :channel-metrics="channelMetrics"
        :suggested-group-channel="suggestedContentMoveGroupChannel"
        :is-channel-of-current-school-year="isChannelOfCurrentSchoolYear"
        @close="showContentMoveConfigurationModal = false"
      />
    </SModal>
  </div>
</template>

<script>
import SduiWaterfallSearch from 'src/components/miscellaneous/ui/common/SduiWaterfallSearch.vue'
import SduiListFileItem from 'src/components/cloud/SduiListFileItem.vue'
import CloudFileDetails from 'src/views/Channels/Cloud/CloudFileDetails.vue'
import { File } from 'src/store/models/channel/index.js'
import isMobileDeviceMixin from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import fileMixin from 'src/mixins/channels/fileMixin.js'

import HiddenFileUpload from 'src/components/miscellaneous/ui/forms/HiddenFileUpload.vue'
import { waitFor } from 'src/config/setup/plugins/waitFor/index.js'
import SduiLoader from 'src/components/miscellaneous/ui/common/SduiLoader.vue'
import Channels from 'src/api/channels.js'
import channelContentMoveMixin from 'src/mixins/channels/channel/channelContentMoveMixin.js'
import SduiContentMoveCard from 'src/components/contentMove/SduiContentMoveCard.vue'
import SduiContentMoveConfiguration from 'src/components/contentMove/SduiContentMoveConfiguration.vue'
import SduiContentMoveDecline from 'src/components/contentMove/SduiContentMoveDecline.vue'
import SProgress from 'src/components/base/SProgress.vue'
import clyTracking from 'src/config/services/countlyTracking/clyTracking.js'
import {defineAsyncComponent} from 'vue'

export default {
  name: 'CloudList',
  components: {
    SduiLoader,
    CloudPathSelector: defineAsyncComponent(() => import('src/components/cloud/CloudPathSelector.vue')),
    HiddenFileUpload,
    CloudFileDetails,
    SduiListFileItem,
    SduiWaterfallSearch,
    SduiContentMoveCard,
    SduiContentMoveConfiguration,
    SduiContentMoveDecline,
    SProgress,
  },

  mixins: [isMobileDeviceMixin, channelContentMoveMixin, fileMixin],
  props: {
    cloudId: { type: Number, default: 0 },
    channel: { type: Object, default: () => ({}) },
    cloud: { type: Object, default: () => ({}) },
    canUpload: { type: Boolean, default: false },
    searchable: { type: Boolean, default: true },
    hideAction: { type: Boolean, default: false },
    fileTypeToFetch: { type: String, default: '' },
    path: { type: String, default: '' },
    currentUuid: {
      type: String,
      default: null,
    },

    showDownloadCurrentFolder: { type: Boolean, default: false },
  },

  data: () => ({
    search: '',
    filesToUpload: [],
    fileToMove: null,
    showFileUpload: false,
    files: [],
    localCloudItems: [],
    fileDetails: {
      id: null,
      type: null,
      api: null,
      isProtected: null,
    },

    loading: false,
    File: File,
    desc: false,
    modals: {
      details: false,
      move: false,
    },

    cloudFilesSelectMultiple: false,
    selectedFiles: [],
    showDeleteConfirmationModal: false,
    showContentMoveDeclineModal: false,
    selectAll: false,
    clearSearch: false,
    forceRefetch: false,
  }),

  computed: {
    // handles url that is given to the endpoint
    url() {
      if (!this.cloudId) return
      return Channels.channelCloudByIdFilesFullPathUrl(
        this.cloudId,
        this.currentUuid,
        this.path,
        this.orderBy,
        this.orderDir,
        this.fileTypeToFetch,
        12,
      )
    },

    emptyStateForFiles() {
      if (this.fileTypeToFetch === 'DIR') return '/img/cloud/no-files-no-face.svg'
      return '/img/cloud/no-files.svg'
    },

    fileParent() {
      return this.files.map((el) => el.meta.parent)[0]
    },

    // route query params
    orderBy() {
      return (this.$route && this.$route.query.orderBy) || 'name'
    },

    orderDir() {
      return (this.$route && this.$route.query.orderDir) || 'asc'
    },

    // returns name based on given orderBy query
    queryName() {
      return this.$t(`cloud.sort.${this.orderDir}.${this.orderBy}`)
    },

    // sort objects the user can search for
    sortActions() {
      return [
        { orderBy: 'name', orderDir: 'asc', icon: 'icon-name-ascending' },
        { orderBy: 'name', orderDir: 'desc', icon: 'icon-name-descending' },
        { orderBy: 'type', orderDir: 'asc', icon: 'icon-type-ascending' },
        { orderBy: 'type', orderDir: 'desc', icon: 'icon-type-descending' },
        { orderBy: 'size', orderDir: 'asc', icon: 'icon-size-ascending' },
        { orderBy: 'size', orderDir: 'desc', icon: 'icon-size-descending' },
        { orderBy: 'created_at', orderDir: 'desc', icon: 'icon-date-descending' },
        { orderBy: 'created_at', orderDir: 'asc', icon: 'icon-date-ascending' },
      ]
    },

    canDisplayDirectoryDownload() {
      return this.files?.length && this.fileParent?.meta?.download_uri && this.showDownloadCurrentFolder
    },
  },

  emits: ['openUpload', 'selectedFolder', 'retryFetch'],

  methods: {
    /*
     * @description: child emits file-item and we add or remove based on selected state
     * @param: uuid: String
     * @param: selected: Boolean
     * */
    setSelectedFilesForMassAction({ uuid, selected }) {
      if (!uuid) return
      if (!selected) {
        this.selectedFiles = this.selectedFiles.filter((el) => el !== uuid)
        return
      }

      if (this.selectedFiles.includes(uuid)) return
      this.selectedFiles = [...this.selectedFiles, uuid]
    },

    // emits folder selection from file-item to the parent, for navigation
    selectedFolder(folder) {
      this.$emit('selectedFolder', folder)
    },

    fetchItems: waitFor('fetching-cloud', async function (page, search) {
      this.search = search
      const url = `${this.url}&page=${page || 1}&search=${search || ''}`
      const saveItems = async (data) => {
        this.files =
          page === 1
            ? data.map((el) => new File(el))
            : this.mergeAndRemoveDuplicates(
              this.files,
              data.map((el) => new File(el)),
              'uuid',
            )
      }

      const { data, network, headers } = await this.$cacheHttp(url)
      const res = await network()

      // if network returns new data, we use it instead of the cached data, to prevent flickering
      const useCachedResponse = !res || !res.data || (res.headers.etag && res.headers.etag === headers.etag)

      await saveItems(useCachedResponse ? data : res.data)

      return useCachedResponse ? { data: data } : res
    }),

    // opens clicked file from file-item
    selectedFile(file) {
      if (this.isMobile) return window.open(file.meta.uri, '_blank')
      window.open(file.meta.uri, '_self')
    },

    // opens a modal which handles file-details
    openDetails({ id, type, api, isProtected, uploadLimitedAt }) {
      this.fileDetails = { id, type, api, isProtected, uploadLimitedAt }
      this.modals.details = true
    },

    emitUploadAndTrack() {
      this.$emit('openUpload')
      clyTracking.trackCloud().start.upload({ type: 'file' })
    },

    /*
     * @description: delete files
     * */
    deleteFiles: waitFor('deleting-files', async function (file) {
      const files = file ? [file] : this.selectedFiles

      !file && (await File.deleteFile(this.cloudId, files))
      this.files = this.files.filter((el) => !files.includes(el.uuid))
      this.selectedFiles = []
      this.cloudFilesSelectMultiple = false
    }),

    /*
     * @description: move files
     */
    moveFile: waitFor('moving-files', async function (data = null) {
      const cloud = data.cloud_id || this.cloudId
      const files = this.fileToMove ? [this.fileToMove] : this.selectedFiles

      await File.moveFile(cloud, files, data.destination)

      // focus back-button after fileMove Modal is closed
      const headerBackBtn = document.querySelector('#header-go-back-button')
      await this.$nextTick()
      setTimeout(() => headerBackBtn?.focus(), 300)

      this.fileToMove = null
      this.selectedFiles = []
      this.files = this.files.filter((el) => !files.includes(el.uuid))
      this.cloudFilesSelectMultiple = false
      this.modals.move = false
    }),

    // selects file which has to be moved and opens the move dialog
    beforeMove(file) {
      this.modals.move = true
      this.fileToMove = file.uuid
    },

    // renames selected file
    renameFile(file) {
      this.files = this.files.map((el) =>
        el.uuid === file.uuid
          ? new File({
            ...file,
            name: file.name,
            path: file.path,
          })
          : el,
      )
    },

    // changes current query parameter and therefore the url which is sent to the endpoint
    handleOrderBy(item) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          orderDir: item.orderDir,
          orderBy: item.orderBy,
        },
      })
    },

    handleOrderDirection() {
      this.desc = !this.desc
      const queryName = this.desc ? 'desc' : 'asc'

      this.$router.replace({
        query: {
          ...this.$route.query,
          orderDir: queryName,
        },
      })
    },

    // passing files from startUploading to the function that created a formdata for the backend to accept the files
    // mapping file types as origin
    startUploading(files) {
      this.uploadFiles(files)
    },

    // we're mapping the files to output them as single arrays
    uploadFiles: waitFor('files', async function (files) {
      const folderUuid = this.$route.params.uuid || ''
      const cloudId = this.cloudId
      const origins = files.map((file) => file.origin)
      const altNames = files.map((file) => file.alt_name)

      const data = new FormData()
      data.append('parent', folderUuid)
      data.append('has_push', this.hasPush)
      origins.forEach((origin) => {
        data.append('files[]', origin)
      })
      altNames.forEach((altName) => {
        data.append('alt_names[]', altName || '')
      })
      const that = this
      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 3 * 60 * 60 * 1000,
        onUploadProgress(progressEvent) {
          const progress = parseInt(Math.round((progressEvent.loaded * 100) / progressEvent.total))
          that.$store.commit(
            'cloud/SET_FILE_UPLOAD_PROGRESS',
            {
              id: 'cloud-upload',
              progress: progress,
            },
            { root: true },
          )
        },
      }

      try {
        const res = await Channels.postChannelCloudByIdUpload(cloudId, data, config)

        // stop callback if user is already gone from page
        if (folderUuid !== (this.$route.params.uuid || '') || cloudId !== this.cloudId) return
        this.files = [...this.files, ...res.data.map((el) => new File(el))]
      } finally {
        that.$store.commit(
          'cloud/SET_FILE_UPLOAD_PROGRESS',
          {
            id: 'cloud-upload',
            progress: 0,
          },
          { root: true },
        )
      }
    }),

    // refetch files when user comes back on page
    refetchOnComeback() {
      if (document.visibilityState === 'hidden') return

      window.scrollTo(0, 0)
      this.forceRefetch = !this.forceRefetch
    },

    // just update file.meta since there are the required properties for download and photoswipe
    updatedFetchedFile(file) {
      this.files = this.files.map((el) =>
        el.uuid === file.uuid
          ? new File({
            ...file,
            meta: file.meta,
          })
          : el,
      )
    },

    mergeAndRemoveDuplicates(array1, array2, property) {
      const mergedArray = array1.concat(array2)
      const uniqueMergedArray = mergedArray.reduce((accumulator, current) => {
        const existingItem = accumulator.find((item) => item[property] === current[property])
        if (!existingItem) {
          accumulator.push(current)
        }
        return accumulator
      }, [])
      return uniqueMergedArray
    },
  },

  watch: {
    url: {
      handler() {
        this.files = []
      },
    },

    '$route.params.uuid': {
      handler() {
        this.clearSearch = !this.clearSearch
      },

      immediate: true,
    },

    cloudFilesSelectMultiple: {
      handler(boolean) {
        if (boolean) return
        this.selectAll = false
        this.selectedFiles = []
      },
    },
  },

  created() {
    window.addEventListener('visibilitychange', this.refetchOnComeback)
  },

  unmounted() {
    window.removeEventListener('visibilitychange', this.refetchOnComeback)
  },
}
</script>

<style scoped lang="scss">
.cloud-choose-file-button {
  min-width: 214px;
  min-height: 50px;
  border-radius: 12px !important;
  font-weight: $font-weight-light !important;
}

.empty-waterfall-search {
  .heading {
    font-size: pxtorem(18) !important;
  }

  .description {
    font-size: pxtorem(14) !important;
  }
}

.dropdown-item-sm {
  @include media($mobile) {
    font-size: pxtorem(14);
  }
}

.bottom-action-bar {
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-top: 1px solid $color-gray-100;

  &.bar-hide {
    opacity: 0;
    transform: translate3d(0, 60px, 0);
    transition: 0.1s ease-in-out;
  }

  &.bar-show {
    opacity: 1 !important;
    transition: 0.1s ease-in-out !important;
    transform: translate3d(0, 0, 0) !important;
  }

  .btn {
    font-size: pxtorem(14);
    padding-bottom: 4px !important;
    padding-top: 6px !important;
    flex: 1;

    .icon {
      font-size: pxtorem(28);
    }
  }
}

.cloud-progress {
  top: 52px;
  left: 0;
  border-radius: 0;
  z-index: 2;
}
</style>
